import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Construction, Plus, Edit, Trash2, Calendar, MapPin, AlertTriangle, CheckCircle2, Pause, Briefcase, Upload, Download, FileText, Folder, X, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
// Table elements now use native HTML for sticky header support

const API = process.env.REACT_APP_BACKEND_URL || '';

const CantieriPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cantieri, setCantieri] = useState([]);
  const [ordiniLavoro, setOrdiniLavoro] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [filteredCantieri, setFilteredCantieri] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroTesto, setFiltroTesto] = useState('');
  const [filtroCitta, setFiltroCitta] = useState('');
  const [filtroReferente, setFiltroReferente] = useState('');
  const [selectedODL, setSelectedODL] = useState('');
  const [uploadingCartella, setUploadingCartella] = useState(null);
  const [selectedCartella, setSelectedCartella] = useState(null);
  const [modalSopralluogoOpen, setModalSopralluogoOpen] = useState(false);
  const [editingSopralluogo, setEditingSopralluogo] = useState(null);
  const [uploadingSopralluogo, setUploadingSopralluogo] = useState(null);
  const [expandedSopralluogo, setExpandedSopralluogo] = useState(null);
  const [importingCantieri, setImportingCantieri] = useState(false);
  const [sopralluogoForm, setSopralluogoForm] = useState({
    tipo_attivita: '',
    data_sopralluogo: '',
    descrizione: '',
    durata_ore: '',
    partecipanti: ''
  });
  const [pendingFilesVerbali, setPendingFilesVerbali] = useState([]);
  const [pendingFilesReport, setPendingFilesReport] = useState([]);
  const [ingegneriSelezionati, setIngegneriSelezionati] = useState([]);
  
  // Refs per la tabella
  const tableContainerRef = useRef(null);
  
  const cartelle = [
    "Nomine", "HSE", "Psc", "Pos Notifica Preliminare", "Verbali",
    "Report fotografici", "Giornali dei Lavori", "Fine Lavori",
    "Materiale trasmesso dal cliente",
    "Verbali di Sopralluogo", "Verbali Riunioni di Coordinamento", "Verbali Direzione dei Lavori"
  ];
  
  const tipiAttivita = [
    "Assistente di Cantiere",
    "CSP",
    "CSE",
    "DL",
    "RL"
  ];
  
  const [formData, setFormData] = useState({
    codice_progetto_cliente: '',
    codice_progetto_main_solution: '',
    nome_progetto: '',
    data_inizio: '',
    data_fine: '',
    ordini_lavoro_ids: [],
    progetti_ids: [],
    referente_cantiere_id: '',
    pm_cliente: '',
    potenza_impianto_kwp: '',
    citta: '',
    provincia: '',
    indirizzo: '',
    stato: 'da_iniziare',
    figure_professionali: [],
    allegati: {}
  });

  const [stats, setStats] = useState({
    totali: 0,
    daIniziare: 0,
    inLavorazione: 0,
    completati: 0,
    sospesi: 0,
    inRitardo: 0
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Funzione per ottenere i tipi di attività disponibili per l'utente corrente
  const getTipiAttivitaDisponibili = () => {
    // Admin e Master vedono tutti i tipi
    if (user.ruolo === 'admin' || user.ruolo === 'master') {
      return tipiAttivita;
    }
    
    // Guest ingegneri vedono solo i tipi per cui sono stati assegnati nel cantiere corrente
    if (!formData.figure_professionali || formData.figure_professionali.length === 0) {
      return [];
    }
    
    // Trova i ruoli dell'utente corrente in questo cantiere
    const ruoliUtente = formData.figure_professionali
      .filter(f => f.user_id === user.id)
      .map(f => f.ruolo);
    
    // Mappa i ruoli ai nomi delle attività
    const mappaRuoliAttivita = {
      'assistente_cantiere': 'Assistente di Cantiere',
      'csp': 'CSP',
      'cse': 'CSE',
      'dl': 'DL',
      'rl': 'RL'
    };
    
    return ruoliUtente.map(ruolo => mappaRuoliAttivita[ruolo]).filter(Boolean);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchCantieri = async () => {
    try {
      setLoading(true);
      
      // Carica cantieri (sempre disponibili)
      const cantieriRes = await axios.get(`${API}/api/cantieri`, getAuthHeaders());
      
      console.log('[DEBUG FRONTEND] Cantieri ricevuti:', cantieriRes.data.length);
      console.log('[DEBUG FRONTEND] Primi cantieri:', cantieriRes.data.slice(0, 2));
      
      setCantieri(cantieriRes.data);
      
      // Carica ordini lavoro (opzionale - solo se l'utente ha accesso)
      try {
        const ordiniRes = await axios.get(`${API}/api/ordini-lavoro`, getAuthHeaders());
        setOrdiniLavoro(ordiniRes.data);
      } catch (ordiniError) {
        console.log('[DEBUG FRONTEND] Ordini lavoro non accessibili (normale per guest)');
        setOrdiniLavoro([]);
      }
      
      // Calcola statistiche
      const oggi = new Date();
      const stats = {
        totali: cantieriRes.data.length,
        daIniziare: cantieriRes.data.filter(c => c.stato === 'da_iniziare').length,
        inLavorazione: cantieriRes.data.filter(c => c.stato === 'in_lavorazione').length,
        completati: cantieriRes.data.filter(c => c.stato === 'completato').length,
        sospesi: cantieriRes.data.filter(c => c.stato === 'sospeso').length,
        inRitardo: cantieriRes.data.filter(c => {
          if (c.stato === 'completato') return false;
          return new Date(c.data_fine) < oggi;
        }).length
      };
      setStats(stats);
    } catch (error) {
      console.error('Errore nel caricamento cantieri:', error);
      setCantieri([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/api/users`, getAuthHeaders());
      // Filtra solo ingegneri
      const ingegneri = response.data.filter(u => u.ambito === 'ingegnere' || u.ruolo === 'admin');
      setUtenti(ingegneri);
    } catch (error) {
      console.log('[DEBUG FRONTEND] Utenti non accessibili (normale per guest)');
      setUtenti([]);
    }
  };

  const caricaDatiDaODL = (odlId) => {
    const odl = ordiniLavoro.find(o => o.id === odlId);
    if (!odl) return;
    
    setFormData({
      ...formData,
      codice_progetto_cliente: odl.codice_progetto_cliente,
      codice_progetto_main_solution: odl.codice_progetto_main_solution,
      nome_progetto: odl.nome_progetto,
      data_inizio: odl.data_inizio_lavoro || '',
      data_fine: odl.data_fine_lavoro || '',
      ordini_lavoro_ids: [odlId]
    });
  };

  useEffect(() => {
    fetchCantieri();
    fetchUtenti();
  }, []);

  useEffect(() => {
    // Applica filtri
    let filtered = cantieri;
    
    console.log('[DEBUG FRONTEND] Filtro stato:', filtroStato);
    console.log('[DEBUG FRONTEND] Cantieri prima filtro:', cantieri.length);
    
    // Filtro per stato
    if (filtroStato !== 'tutti') {
      if (filtroStato === 'in_ritardo') {
        const oggi = new Date();
        filtered = filtered.filter(c => {
          if (c.stato === 'completato') return false;
          return new Date(c.data_fine) < oggi;
        });
      } else {
        filtered = filtered.filter(c => c.stato === filtroStato);
      }
    }
    
    // Filtro per testo (cerca in codice cliente, codice MS, nome progetto)
    if (filtroTesto.trim()) {
      const searchLower = filtroTesto.toLowerCase().trim();
      filtered = filtered.filter(c => 
        (c.codice_progetto_cliente || '').toLowerCase().includes(searchLower) ||
        (c.codice_progetto_main_solution || '').toLowerCase().includes(searchLower) ||
        (c.nome_progetto || '').toLowerCase().includes(searchLower) ||
        (c.pm_cliente || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Filtro per città
    if (filtroCitta.trim()) {
      const cittaLower = filtroCitta.toLowerCase().trim();
      filtered = filtered.filter(c => 
        (c.citta || '').toLowerCase().includes(cittaLower) ||
        (c.provincia || '').toLowerCase().includes(cittaLower) ||
        (c.indirizzo || '').toLowerCase().includes(cittaLower)
      );
    }
    
    // Filtro per referente
    if (filtroReferente.trim()) {
      const refLower = filtroReferente.toLowerCase().trim();
      filtered = filtered.filter(c => 
        (c.referente_cantiere_nome || '').toLowerCase().includes(refLower) ||
        c.figure_professionali?.some(f => (f.nome_completo || '').toLowerCase().includes(refLower))
      );
    }
    
    console.log('[DEBUG FRONTEND] Cantieri dopo filtro:', filtered.length);
    
    setFilteredCantieri(filtered);
  }, [cantieri, filtroStato, filtroTesto, filtroCitta, filtroReferente]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(`${API}/api/cantieri/${formData.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/api/cantieri`, formData, getAuthHeaders());
      }
      
      setModalOpen(false);
      fetchCantieri();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio cantiere:', error);
      alert('Errore nel salvataggio del cantiere');
    }
  };

  const handleEdit = (cantiere) => {
    setFormData({
      ...cantiere,
      figure_professionali: cantiere.figure_professionali || []
    });
    
    // Popola ingegneri selezionati dagli esistenti
    if (cantiere.figure_professionali && cantiere.figure_professionali.length > 0) {
      const uniqueIngegneri = Array.from(
        new Map(cantiere.figure_professionali.map(f => [f.user_id, { user_id: f.user_id, nome_completo: f.nome_completo }])).values()
      );
      setIngegneriSelezionati(uniqueIngegneri);
    } else {
      setIngegneriSelezionati([]);
    }
    
    setEditMode(true);
    setModalOpen(true);
  };

  const handleSelectIngegnere = (utente) => {
    const nomeCompleto = `${utente.nome} ${utente.cognome}`;
    // Aggiungi alla lista ingegneri selezionati
    if (!ingegneriSelezionati.find(i => i.user_id === utente.id)) {
      setIngegneriSelezionati(prev => [...prev, { user_id: utente.id, nome_completo: nomeCompleto }]);
    }
  };

  const handleRemoveIngegnere = (userId) => {
    // Rimuovi dalla lista ingegneri selezionati
    setIngegneriSelezionati(prev => prev.filter(i => i.user_id !== userId));
    // Rimuovi tutti i ruoli associati
    setFormData(prev => ({
      ...prev,
      figure_professionali: prev.figure_professionali.filter(f => f.user_id !== userId)
    }));
  };

  const handleToggleFiguraRuolo = (userId, nomeCompleto, ruolo) => {
    setFormData(prev => {
      const exists = prev.figure_professionali.some(f => f.user_id === userId && f.ruolo === ruolo);
      if (exists) {
        // Rimuovi il ruolo
        return {
          ...prev,
          figure_professionali: prev.figure_professionali.filter(f => !(f.user_id === userId && f.ruolo === ruolo))
        };
      } else {
        // Aggiungi il ruolo
        return {
          ...prev,
          figure_professionali: [...prev.figure_professionali, { user_id: userId, nome_completo: nomeCompleto, ruolo }]
        };
      }
    });
  };

  const hasFiguraRuolo = (userId, ruolo) => {
    return formData.figure_professionali.some(f => f.user_id === userId && f.ruolo === ruolo);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cantiere?')) return;
    
    try {
      await axios.delete(`${API}/api/cantieri/${id}`, getAuthHeaders());
      fetchCantieri();
    } catch (error) {
      console.error('Errore nell\'eliminazione cantiere:', error);
      alert('Errore nell\'eliminazione del cantiere');
    }
  };

  // Import cantieri da Excel
  const handleImportCantieriExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setImportingCantieri(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API}/api/cantieri/import`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`${response.data.message}`);
      fetchCantieri();
    } catch (error) {
      console.error('Errore import cantieri:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImportingCantieri(false);
      event.target.value = '';
    }
  };

  // Scarica template per import cantieri
  const handleDownloadCantieriTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/api/cantieri/import/template`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_cantieri.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Template scaricato!');
    } catch (error) {
      console.error('Errore download template:', error);
      alert('Errore durante il download del template');
    }
  };

  const handleUploadAllegato = async (cartella, file) => {
    if (!formData.id) return;
    
    try {
      setUploadingCartella(cartella);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await axios.post(
        `${API}/api/cantieri/${formData.id}/allegati/${encodeURIComponent(cartella)}`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      alert('Allegato caricato con successo');
    } catch (error) {
      console.error('Errore nel caricamento allegato:', error);
      alert('Errore nel caricamento dell\'allegato');
    } finally {
      setUploadingCartella(null);
    }
  };

  const handleDeleteAllegato = async (cartella, allegatoId) => {
    if (!formData.id) return;
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;
    
    try {
      await axios.delete(
        `${API}/api/cantieri/${formData.id}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`,
        getAuthHeaders()
      );
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      alert('Allegato eliminato con successo');
    } catch (error) {
      console.error('Errore nell\'eliminazione allegato:', error);
      alert('Errore nell\'eliminazione dell\'allegato');
    }
  };

  const handleDownloadAllegato = (cartella, allegatoId, filename) => {
    const url = `${API}/api/cantieri/${formData.id}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.click();
  };

  const handleCreateSopralluogo = async (e) => {
    e.preventDefault();
    if (!formData.id) return;
    
    try {
      const sopralluogoData = {
        ...sopralluogoForm,
        durata_ore: sopralluogoForm.durata_ore ? parseFloat(sopralluogoForm.durata_ore) : null
      };
      
      let sopralluogoId;
      
      if (editingSopralluogo) {
        await axios.put(
          `${API}/api/cantieri/${formData.id}/sopralluoghi/${editingSopralluogo.id}`,
          sopralluogoData,
          getAuthHeaders()
        );
        sopralluogoId = editingSopralluogo.id;
      } else {
        const response = await axios.post(
          `${API}/api/cantieri/${formData.id}/sopralluoghi`,
          sopralluogoData,
          getAuthHeaders()
        );
        sopralluogoId = response.data.sopralluogo.id;
        
        // Upload file in coda (solo per nuovi sopralluoghi)
        if (pendingFilesVerbali.length > 0 || pendingFilesReport.length > 0) {
          // Upload verbali (sia nel sopralluogo che nel cantiere generale)
          for (const file of pendingFilesVerbali) {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            // Upload nel sopralluogo
            await axios.post(
              `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}/allegati/Verbali`,
              formDataUpload,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            // Upload anche nella cartella generale del cantiere
            const formDataUploadCantiere = new FormData();
            formDataUploadCantiere.append('file', file);
            await axios.post(
              `${API}/api/cantieri/${formData.id}/allegati/Verbali`,
              formDataUploadCantiere,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          }
          
          // Upload report fotografici (sia nel sopralluogo che nel cantiere generale)
          for (const file of pendingFilesReport) {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            // Upload nel sopralluogo
            await axios.post(
              `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}/allegati/Report fotografici`,
              formDataUpload,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            // Upload anche nella cartella generale del cantiere
            const formDataUploadCantiere = new FormData();
            formDataUploadCantiere.append('file', file);
            await axios.post(
              `${API}/api/cantieri/${formData.id}/allegati/Report fotografici`,
              formDataUploadCantiere,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          }
        }
      }
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      
      setModalSopralluogoOpen(false);
      resetSopralluogoForm();
      alert(editingSopralluogo ? 'Sopralluogo aggiornato con successo' : 'Sopralluogo creato con successo');
    } catch (error) {
      console.error('Errore nel salvataggio sopralluogo:', error);
      alert('Errore nel salvataggio del sopralluogo');
    }
  };

  const handleEditSopralluogo = (sopralluogo) => {
    setEditingSopralluogo(sopralluogo);
    setSopralluogoForm({
      tipo_attivita: sopralluogo.tipo_attivita,
      data_sopralluogo: sopralluogo.data_sopralluogo,
      descrizione: sopralluogo.descrizione || '',
      durata_ore: sopralluogo.durata_ore || '',
      partecipanti: sopralluogo.partecipanti || ''
    });
    setModalSopralluogoOpen(true);
  };

  const handleDeleteSopralluogo = async (sopralluogoId) => {
    if (!formData.id) return;
    if (!window.confirm('Sei sicuro di voler eliminare questo sopralluogo?')) return;
    
    try {
      await axios.delete(
        `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}`,
        getAuthHeaders()
      );
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      alert('Sopralluogo eliminato con successo');
    } catch (error) {
      console.error('Errore nell\'eliminazione sopralluogo:', error);
      alert('Errore nell\'eliminazione del sopralluogo');
    }
  };

  const handleUploadAllegatoSopralluogo = async (sopralluogoId, cartella, file) => {
    if (!formData.id) return;
    
    try {
      setUploadingSopralluogo(`${sopralluogoId}-${cartella}`);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Upload nel sopralluogo
      await axios.post(
        `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}/allegati/${encodeURIComponent(cartella)}`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Upload anche nella cartella generale del cantiere (sincronizzazione)
      const formDataUploadCantiere = new FormData();
      formDataUploadCantiere.append('file', file);
      await axios.post(
        `${API}/api/cantieri/${formData.id}/allegati/${encodeURIComponent(cartella)}`,
        formDataUploadCantiere,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      alert('Allegato caricato con successo (anche in Gestione Documenti)');
    } catch (error) {
      console.error('Errore nel caricamento allegato:', error);
      alert('Errore nel caricamento dell\'allegato');
    } finally {
      setUploadingSopralluogo(null);
    }
  };

  const handleDeleteAllegatoSopralluogo = async (sopralluogoId, cartella, allegatoId) => {
    if (!formData.id) return;
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;
    
    try {
      await axios.delete(
        `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`,
        getAuthHeaders()
      );
      
      // Refresh cantiere data
      const res = await axios.get(`${API}/api/cantieri/${formData.id}`, getAuthHeaders());
      setFormData(res.data);
      alert('Allegato eliminato con successo');
    } catch (error) {
      console.error('Errore nell\'eliminazione allegato:', error);
      alert('Errore nell\'eliminazione dell\'allegato');
    }
  };

  // Helper per ottenere nomi delle persone per un ruolo specifico
  const getNomiPerRuolo = (figureProfessionali, ruolo) => {
    if (!figureProfessionali || figureProfessionali.length === 0) return [];
    return figureProfessionali
      .filter(f => f.ruolo === ruolo)
      .map(f => f.nome_completo);
  };

  const handleDownloadAllegatoSopralluogo = (sopralluogoId, cartella, allegatoId, filename) => {
    const url = `${API}/api/cantieri/${formData.id}/sopralluoghi/${sopralluogoId}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.click();
  };

  const resetSopralluogoForm = () => {
    setSopralluogoForm({
      tipo_attivita: '',
      data_sopralluogo: '',
      descrizione: '',
      durata_ore: '',
      partecipanti: ''
    });
    setPendingFilesVerbali([]);
    setPendingFilesReport([]);
    setEditingSopralluogo(null);
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/api/cantieri/export/excel`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cantieri_export_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nel download Excel:', error);
      alert('Errore nel download del file Excel');
    }
  };

  const resetForm = () => {
    setFormData({
      codice_progetto_cliente: '',
      codice_progetto_main_solution: '',
      nome_progetto: '',
      data_inizio: '',
      data_fine: '',
      ordini_lavoro_ids: [],
      progetti_ids: [],
      referente_cantiere_id: '',
      pm_cliente: '',
      potenza_impianto_kwp: '',
      citta: '',
      provincia: '',
      indirizzo: '',
      stato: 'da_iniziare',
      figure_professionali: []
    });
    setSelectedODL('');
    setEditMode(false);
    setIngegneriSelezionati([]);
  };

  const getStatoBadgeColor = (stato, dataFine) => {
    if (stato === 'completato') return 'bg-green-100 text-green-700';
    if (stato === 'sospeso') return 'bg-red-100 text-red-700';
    if (stato === 'da_iniziare') return 'bg-yellow-100 text-yellow-700';
    if (stato === 'in_lavorazione') return 'bg-blue-100 text-blue-700';
    
    // Verifica ritardo
    const oggi = new Date();
    if (new Date(dataFine) < oggi) {
      return 'bg-orange-100 text-orange-700';
    }
    
    return 'bg-blue-100 text-blue-700';
  };

  const getStatoIcon = (stato) => {
    if (stato === 'completato') return <CheckCircle2 className="w-4 h-4" />;
    if (stato === 'sospeso') return <Pause className="w-4 h-4" />;
    if (stato === 'in_lavorazione') return <Construction className="w-4 h-4" />;
    if (stato === 'da_iniziare') return <Calendar className="w-4 h-4" />;
    return null;
  };

  // Check Alert AS Built - restituisce info sull'urgenza della consegna AS Built
  const checkAsBuiltAlert = (cantiere) => {
    // Se già consegnato o non c'è data collaudo, nessun alert
    if (cantiere.data_consegna_as_built || !cantiere.data_collaudo_prevista) {
      return null;
    }
    
    const collaudoDate = new Date(cantiere.data_collaudo_prevista);
    const oggi = new Date();
    const giorniRimanenti = Math.ceil((collaudoDate - oggi) / (1000 * 60 * 60 * 24));
    
    // AS Built deve essere consegnato almeno 5 giorni prima del collaudo
    if (giorniRimanenti <= 5) {
      return { type: 'danger', giorni: giorniRimanenti };
    } else if (giorniRimanenti <= 10) {
      return { type: 'warning', giorni: giorniRimanenti };
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Caricamento cantieri...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-white hover:bg-white hover:bg-opacity-20"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold flex items-center gap-3">
                  <Construction className="w-8 h-8" />
                  Cantieri
                </h1>
                <p className="text-sm opacity-90 mt-1">Gestione cantieri e commesse</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleDownloadExcel}
                className="bg-white text-green-600 hover:bg-opacity-90 shadow-lg"
              >
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Esporta Excel
              </Button>
              
              {(user.ruolo === 'admin' || user.ruolo === 'master' || (user.ruolo === 'guest' && user.ambito === 'ingegnere')) && (
                <>
                  <Button
                    variant="outline"
                    className="bg-white/90 text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={handleDownloadCantieriTemplate}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    className="bg-white/90 text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={() => document.getElementById('import-cantieri-excel').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importa Excel
                  </Button>
                  <input
                    id="import-cantieri-excel"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportCantieriExcel}
                    className="hidden"
                  />
                  <Button
                    onClick={() => {
                      resetForm();
                      setModalOpen(true);
                    }}
                    className="bg-white text-orange-600 hover:bg-opacity-90 shadow-lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuovo Cantiere
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-slate-500">
            <p className="text-sm text-slate-600">Totale Cantieri</p>
            <p className="text-3xl font-bold text-slate-700">{stats.totali}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-slate-600">Da Iniziare</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.daIniziare}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-slate-600">In Lavorazione</p>
            <p className="text-3xl font-bold text-blue-600">{stats.inLavorazione}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-slate-600">Completati</p>
            <p className="text-3xl font-bold text-green-600">{stats.completati}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
            <p className="text-sm text-slate-600">Sospesi</p>
            <p className="text-3xl font-bold text-red-600">{stats.sospesi}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
            <p className="text-sm text-slate-600">In Ritardo</p>
            <p className="text-3xl font-bold text-orange-600">{stats.inRitardo}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Ricerca testo */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Cerca (codice, nome, PM)</Label>
              <Input
                placeholder="Cerca..."
                value={filtroTesto}
                onChange={(e) => setFiltroTesto(e.target.value)}
                className="h-9"
              />
            </div>
            
            {/* Filtro stato */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Stato</Label>
              <Select value={filtroStato} onValueChange={setFiltroStato}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti gli stati</SelectItem>
                  <SelectItem value="da_iniziare">Da Iniziare</SelectItem>
                  <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                  <SelectItem value="sospeso">Sospesi</SelectItem>
                  <SelectItem value="completato">Completati</SelectItem>
                  <SelectItem value="in_ritardo">In Ritardo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtro località */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Località</Label>
              <Input
                placeholder="Città, provincia..."
                value={filtroCitta}
                onChange={(e) => setFiltroCitta(e.target.value)}
                className="h-9"
              />
            </div>
            
            {/* Filtro referente */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Referente / Figure</Label>
              <Input
                placeholder="Nome referente..."
                value={filtroReferente}
                onChange={(e) => setFiltroReferente(e.target.value)}
                className="h-9"
              />
            </div>
            
            {/* Reset filtri */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">&nbsp;</Label>
              <Button
                variant="outline"
                className="h-9 w-full"
                onClick={() => {
                  setFiltroStato('tutti');
                  setFiltroTesto('');
                  setFiltroCitta('');
                  setFiltroReferente('');
                }}
              >
                🔄 Reset Filtri
              </Button>
            </div>
          </div>
          
          {/* Contatore risultati */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              Trovati <strong className="text-slate-700">{filteredCantieri.length}</strong> cantieri
              {filteredCantieri.length !== cantieri.length && (
                <span className="text-slate-400"> su {cantieri.length} totali</span>
              )}
            </span>
          </div>
        </div>

        {/* Table con scrollbar e header sticky */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div 
            ref={tableContainerRef}
            className="overflow-auto max-h-[600px]"
          >
              <table style={{ minWidth: '2200px' }} className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 bg-white z-10 shadow-sm border-b">
                  <tr>
                    <th className="h-10 px-2 text-center align-middle font-medium text-slate-600">Azioni</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Codice Main Solution</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Codice Cliente</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Stato</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Nome Progetto</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">PM Cliente</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Referente MS</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Assistente</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">CSP</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">CSE</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">DL</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">RL</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-600">Potenza (kWp)</th>
                  </tr>
                </thead>
                <tbody>
              {filteredCantieri.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-8 text-slate-500">
                    Nessun cantiere trovato
                  </td>
                </tr>
              ) : (
                filteredCantieri.map((cantiere) => {
                  const oggi = new Date();
                  const inRitardo = cantiere.stato !== 'completato' && new Date(cantiere.data_fine) < oggi;
                  
                  return (
                    <tr key={cantiere.id} className="border-b transition-colors hover:bg-slate-50">
                      {/* Azioni - All'inizio della riga */}
                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/cantieri/${cantiere.id}`)}
                            className="hover:bg-blue-50"
                            title="Visualizza dettagli"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          {(user.ruolo === 'admin' || user.ruolo === 'master') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(cantiere.id)}
                              className="hover:bg-red-50"
                              title="Elimina cantiere"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                      
                      {/* Codice Main Solution */}
                      <td className="font-mono font-semibold text-purple-600">
                        {cantiere.codice_progetto_main_solution}
                      </td>
                      
                      {/* Codice Cliente */}
                      <td className="font-mono font-semibold text-indigo-600">
                        {cantiere.codice_progetto_cliente || <span className="text-slate-400">-</span>}
                      </td>
                      
                      {/* Stato + Alert AS Built */}
                      <td>
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatoBadgeColor(cantiere.stato, cantiere.data_fine)}`}>
                            {getStatoIcon(cantiere.stato)}
                            {inRitardo && cantiere.stato === 'in_lavorazione' ? 'In Ritardo' :
                             cantiere.stato === 'da_iniziare' ? 'Da Iniziare' :
                             cantiere.stato === 'in_lavorazione' ? 'In Lavorazione' :
                             cantiere.stato === 'completato' ? 'Completato' :
                             cantiere.stato === 'sospeso' ? 'Sospeso' :
                             cantiere.stato}
                          </span>
                          {/* Alert AS Built */}
                          {(() => {
                            const asBuiltAlert = checkAsBuiltAlert(cantiere);
                            if (asBuiltAlert) {
                              return (
                                <span 
                                  className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit cursor-help ${
                                    asBuiltAlert.type === 'danger' 
                                      ? 'bg-red-100 text-red-700 border border-red-300' 
                                      : 'bg-amber-100 text-amber-700 border border-amber-300'
                                  }`}
                                  title={`AS Built da consegnare entro ${asBuiltAlert.giorni} giorni (5 gg prima del collaudo)`}
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  AS Built
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                      
                      {/* Nome Progetto */}
                      <td className="font-semibold">{cantiere.nome_progetto}</td>
                      
                      {/* PM Cliente */}
                      <td className="text-sm text-slate-600">
                        {cantiere.pm_cliente || <span className="text-slate-400">-</span>}
                      </td>
                      
                      {/* Referente MS */}
                      <td className="text-sm text-slate-600">
                        {cantiere.referente_cantiere_nome || <span className="text-slate-400">-</span>}
                      </td>
                      
                      {/* Colonna Assistente */}
                      <td className="text-xs">
                        {(() => {
                          const nomi = getNomiPerRuolo(cantiere.figure_professionali, 'assistente_cantiere');
                          return nomi.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {nomi.map((nome, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                  {nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      
                      {/* Colonna CSP */}
                      <td className="text-xs">
                        {(() => {
                          const nomi = getNomiPerRuolo(cantiere.figure_professionali, 'csp');
                          return nomi.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {nomi.map((nome, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                  {nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      
                      {/* Colonna CSE */}
                      <td className="text-xs">
                        {(() => {
                          const nomi = getNomiPerRuolo(cantiere.figure_professionali, 'cse');
                          return nomi.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {nomi.map((nome, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                  {nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      
                      {/* Colonna DL */}
                      <td className="text-xs">
                        {(() => {
                          const nomi = getNomiPerRuolo(cantiere.figure_professionali, 'dl');
                          return nomi.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {nomi.map((nome, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                  {nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      
                      {/* Colonna RL */}
                      <td className="text-xs">
                        {(() => {
                          const nomi = getNomiPerRuolo(cantiere.figure_professionali, 'rl');
                          return nomi.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {nomi.map((nome, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                                  {nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      
                      {/* Potenza kWp */}
                      <td className="text-sm font-medium text-amber-600">
                        {cantiere.potenza_impianto_kwp ? `${cantiere.potenza_impianto_kwp} kWp` : <span className="text-slate-400">-</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
              </table>
          </div>
        </div>
      </div>

      {/* Modal Crea/Modifica Cantiere */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              <h2 className="text-2xl font-bold">
                {editMode ? 'Modifica Cantiere' : 'Nuovo Cantiere'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Carica dati da Ordine di Lavoro (solo creazione) */}
              {!editMode && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    <Label className="text-blue-900 font-semibold">Carica dati da Ordine di Lavoro</Label>
                  </div>
                  <Select
                    value={selectedODL}
                    onValueChange={(value) => {
                      setSelectedODL(value);
                      if (value) {
                        caricaDatiDaODL(value);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona un ordine di lavoro..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ordiniLavoro.map((odl) => (
                        <SelectItem key={odl.id} value={odl.id}>
                          {odl.numero_ordine} - {odl.nome_progetto}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-700">
                    Seleziona un ordine per auto-compilare i campi del cantiere (opzionale)
                  </p>
                </div>
              )}

              {/* Collega Ordine di Lavoro (modifica) - Solo 1 OdL per cantiere */}
              {editMode && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-5 h-5 text-green-600" />
                    <Label className="text-green-900 font-semibold">Ordine di Lavoro Collegato</Label>
                  </div>
                  
                  {/* OdL già collegato */}
                  {formData.ordini_lavoro_ids && formData.ordini_lavoro_ids.length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const odlId = formData.ordini_lavoro_ids[0];
                          const odl = ordiniLavoro.find(o => o.id === odlId);
                          return (
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                              📋 {odl ? `${odl.numero_ordine} - ${odl.nome_progetto}` : odlId}
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    ordini_lavoro_ids: []
                                  }));
                                }}
                                className="ml-2 text-green-600 hover:text-red-600 font-bold"
                              >
                                ✕
                              </button>
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-green-600">
                        Per cambiare OdL, rimuovi prima quello attuale
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Dropdown per selezionare OdL */}
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (value) {
                            const odl = ordiniLavoro.find(o => o.id === value);
                            
                            // Aggiorna tutti i campi comuni dall'OdL
                            setFormData(prev => ({
                              ...prev,
                              ordini_lavoro_ids: [value], // Solo 1 OdL
                              // Aggiorna campi comuni se l'OdL ha questi valori
                              codice_progetto_cliente: odl?.codice_progetto_cliente || prev.codice_progetto_cliente,
                              codice_progetto_main_solution: odl?.codice_progetto_main_solution || prev.codice_progetto_main_solution,
                              nome_progetto: odl?.nome_progetto || prev.nome_progetto,
                              pm_cliente: odl?.pm_cliente || prev.pm_cliente,
                              data_inizio: odl?.data_inizio_lavoro || prev.data_inizio,
                              data_fine: odl?.data_fine_lavoro || prev.data_fine
                            }));
                            
                            toast.success(`Dati aggiornati dall'ordine ${odl?.numero_ordine}`);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona ordine di lavoro..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ordiniLavoro.map((odl) => (
                            <SelectItem key={odl.id} value={odl.id}>
                              {odl.numero_ordine} - {odl.nome_progetto}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-green-700">
                        ℹ️ Collegando un OdL, i campi comuni (codici, nome, date, PM Cliente) verranno aggiornati automaticamente
                      </p>
                    </>
                  )}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-800">
                  ℹ️ <strong>Nota:</strong> È possibile creare un cantiere anche senza collegarlo a un ordine di lavoro. Potrai sempre collegare ordini e progetti successivamente.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Codice Progetto Cliente *</Label>
                  <Input
                    value={formData.codice_progetto_cliente}
                    onChange={(e) => setFormData({...formData, codice_progetto_cliente: e.target.value})}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Codice Progetto Main Solution *</Label>
                  <Input
                    value={formData.codice_progetto_main_solution}
                    onChange={(e) => setFormData({...formData, codice_progetto_main_solution: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nome Progetto *</Label>
                <Input
                  value={formData.nome_progetto}
                  onChange={(e) => setFormData({...formData, nome_progetto: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Inizio *</Label>
                  <Input
                    type="date"
                    value={formData.data_inizio}
                    onChange={(e) => setFormData({...formData, data_inizio: e.target.value})}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Data Fine *</Label>
                  <Input
                    type="date"
                    value={formData.data_fine}
                    onChange={(e) => setFormData({...formData, data_fine: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Stato</Label>
                <Select
                  value={formData.stato}
                  onValueChange={(value) => setFormData({...formData, stato: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_iniziare">Da Iniziare</SelectItem>
                    <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Potenza Impianto e Località */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Dati Impianto e Località
                </h3>
                
                <div className="space-y-2">
                  <Label>Potenza Impianto (kWp)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.potenza_impianto_kwp || ''}
                    onChange={(e) => setFormData({...formData, potenza_impianto_kwp: e.target.value})}
                    placeholder="Es: 100.5"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={formData.citta || ''}
                      onChange={(e) => setFormData({...formData, citta: e.target.value})}
                      placeholder="Es: Roma"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={formData.provincia || ''}
                      onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                      placeholder="Es: RM"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Indirizzo</Label>
                  <Input
                    value={formData.indirizzo || ''}
                    onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                    placeholder="Es: Via Roma 1"
                  />
                </div>
              </div>

              {/* PM Cliente */}
              <div className="space-y-2">
                <Label>PM Cliente</Label>
                <Input
                  value={formData.pm_cliente || ''}
                  onChange={(e) => setFormData({...formData, pm_cliente: e.target.value})}
                  placeholder="Nome del Project Manager del cliente"
                />
              </div>

              {/* Referente Main Solution */}
              <div className="space-y-2">
                <Label>Referente Main Solution (opzionale)</Label>
                <Select
                  value={formData.referente_cantiere_id || undefined}
                  onValueChange={(value) => setFormData({...formData, referente_cantiere_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona referente cantiere..." />
                  </SelectTrigger>
                  <SelectContent>
                    {utenti.map(utente => (
                      <SelectItem key={utente.id} value={utente.id}>
                        {utente.nome} {utente.cognome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.referente_cantiere_id && (
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, referente_cantiere_id: ''})}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    ✕ Rimuovi referente MS
                  </button>
                )}
              </div>

              {/* Figure Professionali */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Figure Professionali</Label>
                
                {/* Dropdown per selezionare ingegnere */}
                <div className="flex gap-2">
                  <Select onValueChange={(userId) => {
                    const utente = utenti.find(u => u.id === userId);
                    if (utente) {
                      handleSelectIngegnere(utente);
                    }
                  }}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Seleziona un ingegnere..." />
                    </SelectTrigger>
                    <SelectContent>
                      {utenti.filter(u => !ingegneriSelezionati.some(i => i.user_id === u.id)).map(utente => (
                        <SelectItem key={utente.id} value={utente.id}>
                          {utente.nome} {utente.cognome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lista ingegneri selezionati con checkbox ruoli */}
                <div className="border rounded-lg p-3 max-h-64 overflow-y-auto bg-slate-50 space-y-3">
                  {ingegneriSelezionati.length > 0 ? (
                    <>
                      {ingegneriSelezionati.map(ingegnere => (
                        <div key={ingegnere.user_id} className="border border-slate-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold text-slate-800">{ingegnere.nome_completo}</p>
                            <button
                              type="button"
                              onClick={() => handleRemoveIngegnere(ingegnere.user_id)}
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                            >
                              ✕ Rimuovi
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {['assistente_cantiere', 'csp', 'cse', 'dl', 'rl'].map(ruolo => (
                              <label
                                key={ruolo}
                                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                                  hasFiguraRuolo(ingegnere.user_id, ruolo)
                                    ? 'bg-purple-100 border-2 border-purple-500 text-purple-700'
                                    : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={hasFiguraRuolo(ingegnere.user_id, ruolo)}
                                  onChange={() => handleToggleFiguraRuolo(ingegnere.user_id, ingegnere.nome_completo, ruolo)}
                                  className="w-3 h-3"
                                />
                                <span className="font-medium">
                                  {ruolo === 'assistente_cantiere' ? 'Assistente' :
                                   ruolo === 'csp' ? 'CSP' :
                                   ruolo === 'cse' ? 'CSE' :
                                   ruolo === 'dl' ? 'DL' :
                                   ruolo === 'rl' ? 'RL' : ruolo}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nessun ingegnere selezionato. Usa il menu a tendina sopra per aggiungere.</p>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Ingegneri selezionati: {ingegneriSelezionati.length}
                </p>
              </div>

              {/* Gestione Allegati - Solo in modalità modifica */}
              {editMode && formData.id && (
                <div className="border-t-2 border-slate-200 pt-6 mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Folder className="w-6 h-6 text-orange-600" />
                    <h3 className="text-lg font-bold text-slate-900">Gestione Documenti</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                    {cartelle.map((cartella) => {
                      const allegatiCartella = formData.allegati?.[cartella] || [];
                      const isUploading = uploadingCartella === cartella;
                      
                      return (
                        <div key={cartella} className="border-2 border-slate-200 rounded-lg p-4 bg-slate-50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Folder className="w-5 h-5 text-orange-500" />
                              <span className="font-semibold text-slate-900">{cartella}</span>
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                {allegatiCartella.length} file
                              </span>
                            </div>
                            
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleUploadAllegato(cartella, file);
                                  }
                                }}
                                disabled={isUploading}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="flex items-center gap-1"
                                disabled={isUploading}
                                onClick={(e) => e.currentTarget.previousElementSibling.click()}
                              >
                                <Upload className="w-4 h-4" />
                                {isUploading ? 'Caricamento...' : 'Carica'}
                              </Button>
                            </label>
                          </div>
                          
                          {/* Lista Allegati */}
                          {allegatiCartella.length > 0 && (
                            <div className="space-y-2">
                              {allegatiCartella.map((allegato) => (
                                <div key={allegato.id} className="bg-white border border-slate-200 rounded p-2 flex items-center justify-between">
                                  <div className="flex items-center gap-2 flex-1">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm text-slate-700 truncate">{allegato.filename}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDownloadAllegato(cartella, allegato.id, allegato.filename)}
                                      className="hover:bg-blue-50"
                                    >
                                      <Download className="w-4 h-4 text-blue-600" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteAllegato(cartella, allegato.id)}
                                      className="hover:bg-red-50"
                                    >
                                      <X className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Gestione Sopralluoghi - Solo in modalità modifica */}
              {editMode && formData.id && (
                <div className="border-t-2 border-slate-200 pt-6 mt-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                      <h3 className="text-lg font-bold text-slate-900">Sopralluoghi</h3>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {formData.sopralluoghi?.length || 0} sopralluoghi
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        resetSopralluogoForm();
                        setModalSopralluogoOpen(true);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Nuovo Sopralluogo
                    </Button>
                  </div>

                  {/* Lista Sopralluoghi */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {formData.sopralluoghi && formData.sopralluoghi.length > 0 ? (
                      formData.sopralluoghi.map((sopralluogo) => (
                        <div key={sopralluogo.id} className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                                  {sopralluogo.tipo_attivita}
                                </span>
                                <span className="text-sm text-slate-500">
                                  {new Date(sopralluogo.data_sopralluogo).toLocaleDateString('it-IT')}
                                </span>
                                {sopralluogo.durata_ore && (
                                  <span className="text-sm text-slate-500">• {sopralluogo.durata_ore}h</span>
                                )}
                              </div>
                              {sopralluogo.descrizione && (
                                <p className="text-sm text-slate-700 mb-2">{sopralluogo.descrizione}</p>
                              )}
                              {sopralluogo.partecipanti && (
                                <p className="text-xs text-slate-500">Partecipanti: {sopralluogo.partecipanti}</p>
                              )}
                              
                              {/* Pulsante Espandi Allegati */}
                              <div className="mt-3">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setExpandedSopralluogo(expandedSopralluogo === sopralluogo.id ? null : sopralluogo.id)}
                                  className="text-xs"
                                >
                                  <Folder className="w-3 h-3 mr-1" />
                                  {expandedSopralluogo === sopralluogo.id ? 'Nascondi' : 'Mostra'} Allegati
                                </Button>
                              </div>
                            </div>
                            <div className="flex gap-1 ml-4">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditSopralluogo(sopralluogo)}
                                className="hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteSopralluogo(sopralluogo.id)}
                                className="hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Sezione Allegati Espandibile */}
                          {expandedSopralluogo === sopralluogo.id && (
                            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                              {['Verbali', 'Report fotografici'].map((cartella) => {
                                const allegatiCartella = sopralluogo.allegati?.[cartella] || [];
                                const isUploading = uploadingSopralluogo === `${sopralluogo.id}-${cartella}`;
                                
                                return (
                                  <div key={cartella} className="bg-slate-50 border border-slate-200 rounded p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Folder className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm font-semibold text-slate-900">{cartella}</span>
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                                          {allegatiCartella.length}
                                        </span>
                                      </div>
                                      
                                      <label className="cursor-pointer">
                                        <input
                                          type="file"
                                          className="hidden"
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              handleUploadAllegatoSopralluogo(sopralluogo.id, cartella, file);
                                            }
                                          }}
                                          disabled={isUploading}
                                        />
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          className="text-xs"
                                          disabled={isUploading}
                                          onClick={(e) => e.currentTarget.previousElementSibling.click()}
                                        >
                                          <Upload className="w-3 h-3 mr-1" />
                                          {isUploading ? 'Caricamento...' : 'Carica'}
                                        </Button>
                                      </label>
                                    </div>
                                    
                                    {/* Lista Allegati */}
                                    {allegatiCartella.length > 0 && (
                                      <div className="space-y-1">
                                        {allegatiCartella.map((allegato) => (
                                          <div key={allegato.id} className="bg-white border border-slate-200 rounded p-2 flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2 flex-1">
                                              <FileText className="w-3 h-3 text-blue-600" />
                                              <span className="text-slate-700 truncate">{allegato.filename}</span>
                                            </div>
                                            <div className="flex gap-1">
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDownloadAllegatoSopralluogo(sopralluogo.id, cartella, allegato.id, allegato.filename)}
                                                className="p-1 h-auto hover:bg-blue-50"
                                              >
                                                <Download className="w-3 h-3 text-blue-600" />
                                              </Button>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDeleteAllegatoSopralluogo(sopralluogo.id, cartella, allegato.id)}
                                                className="p-1 h-auto hover:bg-red-50"
                                              >
                                                <X className="w-3 h-3 text-red-600" />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                        Nessun sopralluogo registrato
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">
                  {editMode ? 'Aggiorna' : 'Crea'} Cantiere
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setModalOpen(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annulla
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Sopralluogo */}
      {modalSopralluogoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <h2 className="text-2xl font-bold">
                {editingSopralluogo ? 'Modifica Sopralluogo' : 'Nuovo Sopralluogo'}
              </h2>
            </div>

            <form onSubmit={handleCreateSopralluogo} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Tipo Attività *</Label>
                <Select
                  value={sopralluogoForm.tipo_attivita}
                  onValueChange={(value) => setSopralluogoForm({...sopralluogoForm, tipo_attivita: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipo attività..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getTipiAttivitaDisponibili().map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {getTipiAttivitaDisponibili().length === 0 && user.ruolo !== 'admin' && user.ruolo !== 'master' && (
                  <p className="text-xs text-red-500 mt-1">
                    Non hai ruoli assegnati in questo cantiere. Contatta un amministratore.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Data Sopralluogo *</Label>
                <Input
                  type="date"
                  value={sopralluogoForm.data_sopralluogo}
                  onChange={(e) => setSopralluogoForm({...sopralluogoForm, data_sopralluogo: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Durata (ore)</Label>
                <Input
                  type="number"
                  step="0.5"
                  placeholder="Es: 2.5"
                  value={sopralluogoForm.durata_ore}
                  onChange={(e) => setSopralluogoForm({...sopralluogoForm, durata_ore: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Partecipanti</Label>
                <Input
                  placeholder="Nomi dei partecipanti"
                  value={sopralluogoForm.partecipanti}
                  onChange={(e) => setSopralluogoForm({...sopralluogoForm, partecipanti: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Descrizione / Note</Label>
                <textarea
                  className="w-full border border-slate-300 rounded-md p-2 min-h-[100px]"
                  placeholder="Descrivi le attività svolte durante il sopralluogo..."
                  value={sopralluogoForm.descrizione}
                  onChange={(e) => setSopralluogoForm({...sopralluogoForm, descrizione: e.target.value})}
                />
              </div>

              {/* Sezione Allegati - Solo per nuovi sopralluoghi */}
              {!editingSopralluogo && (
                <div className="border-t-2 border-slate-200 pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Folder className="w-5 h-5 text-blue-600" />
                    <h4 className="text-sm font-bold text-slate-900">Allegati (Opzionale)</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Verbali */}
                    <div className="bg-slate-50 border border-slate-200 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-900">Verbali</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {pendingFilesVerbali.length} file
                        </span>
                      </div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          setPendingFilesVerbali([...pendingFilesVerbali, ...newFiles]);
                        }}
                        className="text-xs"
                      />
                      {pendingFilesVerbali.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {pendingFilesVerbali.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white p-1 rounded text-xs">
                              <span className="truncate flex-1">{file.name}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setPendingFilesVerbali(pendingFilesVerbali.filter((_, i) => i !== idx))}
                                className="p-1 h-auto"
                              >
                                <X className="w-3 h-3 text-red-600" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Report fotografici */}
                    <div className="bg-slate-50 border border-slate-200 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-900">Report fotografici</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {pendingFilesReport.length} file
                        </span>
                      </div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          setPendingFilesReport([...pendingFilesReport, ...newFiles]);
                        }}
                        className="text-xs"
                      />
                      {pendingFilesReport.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {pendingFilesReport.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white p-1 rounded text-xs">
                              <span className="truncate flex-1">{file.name}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setPendingFilesReport(pendingFilesReport.filter((_, i) => i !== idx))}
                                className="p-1 h-auto"
                              >
                                <X className="w-3 h-3 text-red-600" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  {editingSopralluogo ? 'Aggiorna' : 'Crea'} Sopralluogo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setModalSopralluogoOpen(false);
                    resetSopralluogoForm();
                  }}
                  className="flex-1"
                >
                  Annulla
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CantieriPage;
